import React, {useMemo, useState} from "react";
import {Table} from "react-bootstrap";
import {router} from "@inertiajs/react";
import {PiCaretDown, PiCaretUp, PiCaretUpDown} from "react-icons/pi";
import useStatisticsData from "../Statistics/useStatisticsData.js";
import {formatNumber, formatPercent} from "../Statistics/format.js";
import {bereichFarbe, formatDateTimeDE, formatDuration} from "./format.js";
import {BereichLink, MitarbeiterLink} from "./Links.jsx";
import NutzungSparkline from "./NutzungSparkline.jsx";
import NutzungWidget from "./NutzungWidget.jsx";

const SPALTEN = [
    {key: "name", label: "Mitarbeiter", sortable: true, align: "start"},
    {key: "bereiche", label: "Bereiche", sortable: false, align: "start"},
    {key: "aktive_sekunden", label: "Aktive Zeit", sortable: true, align: "end", format: formatDuration},
    {key: "anwesend_sekunden", label: "Anwesenheit", sortable: true, align: "end", format: formatDuration},
    {key: "aktionen", label: "Aktionen", sortable: true, align: "end", format: formatNumber},
    {key: "aenderungen", label: "Änderungen", sortable: true, align: "end", format: formatNumber},
    {key: "sitzungen", label: "Sitzungen", sortable: true, align: "end", format: formatNumber},
    {key: "letzte_anmeldung", label: "Letzte Anmeldung", sortable: true, align: "start", format: formatDateTimeDE},
    {key: "verlauf", label: "Verlauf (14 Tage)", sortable: false, align: "center"},
];

function gehezuProfil(userId) {
    router.get(`/nutzung/mitarbeiter/${userId}`);
}

/** Bis zu drei farbige Bereichsmarken je Mitarbeiter, absteigend nach
 * Ereignissen - die vollständige Aufstellung bleibt dem Einzelprofil
 * vorbehalten, hier reicht ein schneller Überblick je Zeile. */
function BereichMarken({bereiche, loading}) {
    const rows = Array.isArray(bereiche) ? [...bereiche].sort((a, b) => (b.ereignisse || 0) - (a.ereignisse || 0)) : [];
    if (loading) {
        return <span className="text-muted small">…</span>;
    }
    if (rows.length === 0) {
        return <span className="text-muted">–</span>;
    }
    return (
        <div className="d-flex flex-wrap gap-1">
            {rows.slice(0, 3).map((eintrag) => (
                <span
                    key={eintrag.bereich}
                    className="badge fw-normal d-inline-flex align-items-center gap-1"
                    style={{backgroundColor: `${bereichFarbe(eintrag.bereich)}22`, color: bereichFarbe(eintrag.bereich)}}
                >
                    <BereichLink bereich={eintrag.bereich} className="text-decoration-none" style={{color: "inherit"}} />
                    {eintrag.anteil != null && <span className="text-muted">{formatPercent(eintrag.anteil * 100, 0)}</span>}
                </span>
            ))}
        </div>
    );
}

/**
 * Vollständige Mitarbeitertabelle - clientseitig sortierbar (der Endpunkt
 * liefert alle Mitarbeiter des Zeitraums auf einmal, keine Server-
 * paginierung, siehe Spezifikation Abschnitt 9). Klick auf eine Zeile
 * öffnet das Profil (/nutzung/mitarbeiter/{user}).
 */
export default function NutzungMitarbeiterTabelle({filters, bereicheProps = {}, bereicheLoading = false}) {
    const {data, loading, error} = useStatisticsData("/nutzung/daten/mitarbeiter", filters);
    const [sort, setSort] = useState({key: "aktive_sekunden", dir: "desc"});

    const rows = useMemo(() => {
        const liste = Array.isArray(data) ? [...data] : [];
        const {key, dir} = sort;
        liste.sort((a, b) => {
            let av = a[key];
            let bv = b[key];
            if (key === "name") {
                av = (av || "").toLocaleLowerCase("de-DE");
                bv = (bv || "").toLocaleLowerCase("de-DE");
            } else if (key === "letzte_anmeldung") {
                av = av ? new Date(av).getTime() : 0;
                bv = bv ? new Date(bv).getTime() : 0;
            } else {
                av = av || 0;
                bv = bv || 0;
            }
            if (av < bv) {
                return dir === "asc" ? -1 : 1;
            }
            if (av > bv) {
                return dir === "asc" ? 1 : -1;
            }
            return 0;
        });
        return liste;
    }, [data, sort]);

    const toggleSort = (key) => {
        setSort((current) => {
            if (current.key !== key) {
                return {key, dir: key === "name" ? "asc" : "desc"};
            }
            return {key, dir: current.dir === "asc" ? "desc" : "asc"};
        });
    };

    return (
        <NutzungWidget
            title="Mitarbeiter"
            loading={loading}
            error={error}
            empty={!loading && !error && rows.length === 0}
            height={420}
        >
            <div style={{overflowX: "auto"}}>
                <Table hover size="sm" className="align-middle mb-0">
                    <thead>
                        <tr>
                            {SPALTEN.map((spalte) => (
                                <th
                                    key={spalte.key}
                                    className={spalte.align === "end" ? "text-end" : spalte.align === "center" ? "text-center" : ""}
                                    style={spalte.sortable ? {cursor: "pointer"} : undefined}
                                    onClick={spalte.sortable ? () => toggleSort(spalte.key) : undefined}
                                >
                                    <span className="d-inline-flex align-items-center gap-1">
                                        {spalte.label}
                                        {spalte.sortable && (sort.key === spalte.key ? (
                                            sort.dir === "asc" ? <PiCaretUp aria-hidden="true" /> : <PiCaretDown aria-hidden="true" />
                                        ) : (
                                            <PiCaretUpDown className="text-muted" aria-hidden="true" />
                                        ))}
                                    </span>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr
                                key={row.user_id}
                                role="button"
                                tabIndex={0}
                                style={{cursor: "pointer"}}
                                onClick={() => gehezuProfil(row.user_id)}
                                onKeyDown={(event) => {
                                    if (event.key === "Enter" || event.key === " ") {
                                        event.preventDefault();
                                        gehezuProfil(row.user_id);
                                    }
                                }}
                            >
                                <td onClick={(event) => event.stopPropagation()}>
                                    <MitarbeiterLink id={row.user_id} name={row.name} className="fw-semibold" />
                                </td>
                                <td>
                                    <BereichMarken bereiche={bereicheProps[String(row.user_id)]} loading={bereicheLoading} />
                                </td>
                                <td className="text-end">{formatDuration(row.aktive_sekunden)}</td>
                                <td className="text-end">{formatDuration(row.anwesend_sekunden)}</td>
                                <td className="text-end">{formatNumber(row.aktionen)}</td>
                                <td className="text-end">{formatNumber(row.aenderungen)}</td>
                                <td className="text-end">{formatNumber(row.sitzungen)}</td>
                                <td>{formatDateTimeDE(row.letzte_anmeldung)}</td>
                                <td className="text-center">
                                    <NutzungSparkline values={row.verlauf} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
        </NutzungWidget>
    );
}
